import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { Sidebar } from '../components/layout/Sidebar';
import { Header } from '../components/layout/Header';
import { OrderForm } from '../components/trading/OrderForm';
import { OrderBook } from '../components/trading/OrderBook';
import { Tabs, TabPanel } from '../components/ui/Tabs';
import { useKrakenMarket } from '../hooks/useKrakenMarket';
import { BalancesTable } from '../components/trading/BalancesTable';
import { PositionsTable } from '../components/trading/PositionsTable';
import { OpenOrdersTable } from '../components/trading/OpenOrdersTable';
import { TradesTable } from '../components/trading/TradesTable';
import { ClosedOrdersTable } from '../components/trading/ClosedOrdersTable';
import { TradingViewChart } from '../components/trading/TradingViewChart';
import { ChevronDownIcon, ChevronUpIcon, BarChart3Icon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function TradingPage() {
  const {
    ticker,
    bids,
    asks,
    trades,
    loading,
    error
  } = useKrakenMarket('XBTUSD', 5000);
  const [tradingMode, setTradingMode] = useState<'spot' | 'margin' | 'futures'>('spot');
  const [activeTab, setActiveTab] = useState(() => tradingMode === 'spot' ? 'open-orders' : 'positions');
  const [mobileChartOpen, setMobileChartOpen] = useState(false);
  const [chartInterval, setChartInterval] = useState('15');
  const accountBalanceUSD = 79.19;
  const spread = bids.length > 0 && asks.length > 0 ? asks[0].price - bids[0].price : undefined;
  const spreadPercent = spread && bids.length > 0 ? spread / bids[0].price * 100 : undefined;
  // Sync activeTab when tradingMode changes
  useEffect(() => {
    const defaultTab = tradingMode === 'spot' ? 'open-orders' : 'positions';
    setActiveTab(defaultTab);
  }, [tradingMode]);
  return <div className="flex h-screen bg-background-primary overflow-hidden" data-id="element-2479">
      <Sidebar data-id="element-2480" />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0 mesh-bg" data-id="element-2481">
        <Header currentPrice={ticker?.price} indexPrice={ticker?.price ? ticker.price + 12.6 : 95453.7} priceChange={ticker?.change} priceChangePercent={ticker?.changePercent} volume24h={ticker ? `${(ticker.volume / 1000000).toFixed(0)}M` : '205M'} volume24hBTC={ticker ? `${(ticker.volume / (ticker.price * 1000)).toFixed(2)}K` : '2.15K'} accountBalance={accountBalanceUSD} data-id="element-2482" />

        {/* Trading Mode Selector */}
        <div className="px-4 py-1.5" data-id="element-2483">
          <div className="flex items-center gap-0.5 w-fit" data-id="element-2484">
            <button onClick={() => setTradingMode('spot')} className={`px-4 py-1 text-xs font-medium rounded-md transition-all ${tradingMode === 'spot' ? 'text-text-primary bg-white/[0.06]' : 'bg-transparent text-text-tertiary hover:text-text-secondary'}`} data-id="element-2485">
              Spot
            </button>
            <button onClick={() => setTradingMode('margin')} className={`px-4 py-1 text-xs font-medium rounded-md transition-all ${tradingMode === 'margin' ? 'text-text-primary bg-white/[0.06]' : 'bg-transparent text-text-tertiary hover:text-text-secondary'}`} data-id="element-2486">
              Margin
            </button>
            <button onClick={() => setTradingMode('futures')} className={`px-4 py-1 text-xs font-medium rounded-md transition-all ${tradingMode === 'futures' ? 'text-text-primary bg-white/[0.06]' : 'bg-transparent text-text-tertiary hover:text-text-secondary'}`} data-id="element-2487">
              Futures
            </button>
          </div>
        </div>

        {/* Trading Interface - Card-based layout */}
        <div className="flex-1 flex flex-col overflow-y-auto lg:overflow-hidden p-2 gap-2 relative z-0" style={{
        background: 'radial-gradient(ellipse at 15% 5%, rgba(122, 46, 255, 0.06) 0%, transparent 50%), radial-gradient(ellipse at 85% 90%, rgba(0, 200, 127, 0.04) 0%, transparent 50%)'
      }} data-id="element-2488">
          {/* Top Row: Chart | Order Book | Order Form */}
          <div className="lg:flex-1 flex flex-col lg:flex-row gap-2 min-h-0" data-id="element-2489">
            {/* Mobile Order Form (shown first on mobile for primary action) */}
            <div className="lg:hidden glass-border rounded-2xl overflow-hidden" data-id="element-2490">
              <OrderForm mode={tradingMode} currentPrice={ticker?.price} availableBalance={accountBalanceUSD} data-id="element-2491" />
            </div>

            {/* Mobile Chart - Collapsible Panel */}
            <div className="lg:hidden glass-border rounded-2xl overflow-hidden" data-id="element-2492">
              <button onClick={() => setMobileChartOpen(prev => !prev)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-text-primary" data-id="element-2493">
                <div className="flex items-center gap-2" data-id="element-2494">
                  <BarChart3Icon size={16} className="text-text-secondary" data-id="element-2495" />
                  <span data-id="element-2496">Chart</span>
                  <span className="text-xs text-text-tertiary font-normal" data-id="element-2497">
                    BTC/USD
                  </span>
                </div>
                {mobileChartOpen ? <ChevronUpIcon size={16} className="text-text-secondary" data-id="element-2498" /> : <ChevronDownIcon size={16} className="text-text-secondary" data-id="element-2499" />}
              </button>
              <AnimatePresence initial={false} data-id="element-2500">
                {mobileChartOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease: [0.4, 0, 0.2, 1]
              }} className="overflow-hidden" data-id="element-2501">
                    <div className="flex items-center gap-1 px-3 py-2 border-t border-b border-[rgba(255,255,255,0.04)] bg-background-secondary/50 overflow-x-auto" data-id="element-2502">
                      {['1', '5', '15', '30', '60', '240', '1D'].map(iv => <button key={iv} onClick={() => setChartInterval(iv)} className={`px-2.5 py-1 text-[11px] font-medium rounded transition-colors whitespace-nowrap ${chartInterval === iv ? 'bg-brand-purple/20 text-brand-purple-light' : 'text-text-tertiary hover:text-text-secondary hover:bg-white/5'}`} data-id="element-2503">
                          {iv === '1' ? '1m' : iv === '5' ? '5m' : iv === '15' ? '15m' : iv === '30' ? '30m' : iv === '60' ? '1H' : iv === '240' ? '4H' : '1D'}
                        </button>)}
                    </div>
                    <div className="h-[350px]" data-id="element-2504">
                      <TradingViewChart symbol="BITSTAMP:BTCUSD" theme="dark" interval={chartInterval} data-id="element-2505" />
                    </div>
                    <div className="h-[300px] border-t border-[rgba(255,255,255,0.04)] overflow-hidden" data-id="element-2506">
                      <OrderBook bids={bids} asks={asks} spread={spread} spreadPercent={spreadPercent} trades={trades} data-id="element-2507" />
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Desktop Chart Card (flex-1, always visible) */}
            <div className="hidden lg:flex flex-1 flex-col min-w-0 min-h-0 glass-border rounded-2xl overflow-hidden" data-id="element-2508">
              {/* Chart toolbar */}
              <div className="flex items-center gap-1 px-3 py-2 border-b border-border-subtle bg-background-secondary/50" data-id="element-2509">
                {['1', '5', '15', '30', '60', '240', '1D'].map(iv => <button key={iv} onClick={() => setChartInterval(iv)} className={`px-2.5 py-1 text-[11px] font-medium rounded transition-colors ${chartInterval === iv ? 'bg-brand-purple/20 text-brand-purple-light' : 'text-text-tertiary hover:text-text-secondary hover:bg-white/5'}`} data-id="element-2510">
                    {iv === '1' ? '1m' : iv === '5' ? '5m' : iv === '15' ? '15m' : iv === '30' ? '30m' : iv === '60' ? '1H' : iv === '240' ? '4H' : '1D'}
                  </button>)}
                <div className="w-px h-4 bg-border-subtle mx-1" data-id="element-2511" />
                <span className="text-[11px] text-text-tertiary" data-id="element-2512">BTC/USD</span>
              </div>
              <div className="flex-1 min-h-0" data-id="element-2513">
                <TradingViewChart symbol="BITSTAMP:BTCUSD" theme="dark" interval={chartInterval} data-id="element-2514" />
              </div>
            </div>

            {/* Middle Panel - Order Book Card */}
            <div className="hidden lg:flex w-[280px] flex-shrink-0 glass-border rounded-2xl overflow-hidden" data-id="element-2515">
              <div className="w-full h-full overflow-hidden" data-id="element-2516">
                <OrderBook bids={bids} asks={asks} spread={spread} spreadPercent={spreadPercent} trades={trades} data-id="element-2517" />
              </div>
            </div>

            {/* Right Panel - Order Form Card */}
            <div className="hidden lg:flex w-[280px] flex-shrink-0 glass-border rounded-2xl overflow-hidden" data-id="element-2518">
              <div className="w-full h-full overflow-y-auto" data-id="element-2519">
                <OrderForm mode={tradingMode} currentPrice={ticker?.price} availableBalance={accountBalanceUSD} data-id="element-2520" />
              </div>
            </div>
          </div>

          {/* Bottom Panel - Positions/Orders Card */}
          <div className="h-[200px] flex-shrink-0 glass-border rounded-2xl overflow-hidden flex flex-col" data-id="element-2521">
            <Tabs tabs={[...(tradingMode !== 'spot' ? [{
            id: 'positions',
            label: 'Positions',
            badge: '1'
          }] : []), {
            id: 'open-orders',
            label: 'Open orders',
            badge: '1'
          }, {
            id: 'closed-orders',
            label: 'Closed'
          }, {
            id: 'alerts',
            label: 'Alerts'
          }]} defaultTab={activeTab} onTabChange={setActiveTab} data-id="element-2522" />
            <div className="flex-1 overflow-auto" data-id="element-2523">
              {activeTab === 'positions' && tradingMode !== 'spot' && <PositionsTable data-id="element-2524" />}
              {activeTab === 'open-orders' && <OpenOrdersTable data-id="element-2525" />}
              {activeTab === 'closed-orders' && <ClosedOrdersTable data-id="element-2526" />}
              {activeTab === 'alerts' && <div className="flex flex-col items-center justify-center h-full" data-id="element-2527">
                  <div className="w-12 h-12 rounded-full bg-background-elevated flex items-center justify-center mb-3" data-id="element-2528">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-text-tertiary" data-id="element-2529">
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" data-id="element-2530" />
                      <path d="M13.73 21a2 2 0 0 1-3.46 0" data-id="element-2531" />
                    </svg>
                  </div>
                  <p className="text-sm text-text-secondary" data-id="element-2532">
                    No active alerts.
                  </p>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}